import { createStore } from '@updater-core/lib/store';
import { loggerMiddleware } from '@updater-core/lib/logger';
import { track, TRACK, CLICK } from '@updater-core/lib/tracker';
import { redirectIntoQuickSetup } from '@updater-core/redirects/quickSetup';
import { redirectIntoConsumerApp } from '@updater-core/redirects/consumerApp';
import { ENVIRONMENT } from '@updater-core/lib/environment';
import { createLogger } from './logger';

const { log } = createLogger('[ACTION]');
const { log: storeLog } = createLogger('[STORE] state update');

function validateEmail(email) {
  const EMAIL_VALIDATION_PATTERN =
    /^[A-Z0-9.!#$%&'*+/=?^_`{|}~\-"\\]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
  const valid = email.match(EMAIL_VALIDATION_PATTERN);
  return {
    valid: !!valid,
    message: valid ? '' : 'Please enter a valid email address.',
  };
}

function validatePassword(password) {
  const hasMinCharacters = {
    pattern: /^.*(?=.{6,})/,
    error: 'Password must be at least 6 characters.',
  };

  const hasNumber = {
    pattern: /^.*(?=.*[0-9])/,
    error: 'Password must have at least one number.',
  };

  const hasLetter = {
    pattern: /^.*(?=.*[a-zA-Z])/,
    error: 'Password must have at least one letter.',
  };

  const errorArray = [hasMinCharacters, hasNumber, hasLetter]
    .filter(({ pattern }) => !pattern.test(password))
    .map(({ error }) => error);

  const isValid = errorArray.length === 0;

  return {
    valid: isValid,
    message: errorArray[0],
  };
}

export default function createInteractor({
  interactors: { session, router, daisyChain },
}) {
  const sessionState = session.getState();
  const store = createStore(
    {
      email: '',
      password: '',
      errorMessage: null,
      showResetPasswordSuccessMsg:
        session.selectors.hasResetPassword(sessionState),
      showForgotPasswordSuccessMsg:
        session.selectors.hasForgotPassword(sessionState),
    },
    [loggerMiddleware(storeLog)]
  );

  function handleForgotPassword(event) {
    log('upd.sign_in.event.forgot_password.clicked');
    event.preventDefault();
    router.go('/forgot-password');
  }

  function handleEmailChange(email) {
    log('upd.sign_in.event.email.changed');
    store.setState({
      email,
      emailValidation: validateEmail(email),
      errorMessage: '',
    });
  }

  function handlePasswordChange(password) {
    log('upd.sign_in.event.password.changed');
    store.setState({
      password,
      passwordValidation: validatePassword(password),
      errorMessage: '',
    });
  }

  function handleSubmit() {
    log('upd.sign_in.event.submitted');
    const { email, password, submitting } = store.getState();

    // Prevent enter-key from resubmitting.
    if (submitting) {
      return undefined;
    }

    const emailValidation = validateEmail(email);
    const passwordValidation = validatePassword(password);

    store.setState({
      submittedOnce: true,
      emailValidation,
      passwordValidation,
    });

    if (!emailValidation.valid || !passwordValidation.valid) {
      log('upd.sign_in.event.invalid.form');
      return Promise.resolve(false);
    }

    const initialPath = router.getInitialPath();
    const isDeepLinkablePath =
      initialPath &&
      ![/sign-in/, /reset-password/, /sign-out/].some((path) =>
        initialPath.match(path)
      );

    const deeplinkPath = isDeepLinkablePath ? initialPath : '/';
    track({
      verb: CLICK,
      object: 'sign_in_cta',
      segmentEventName: 'Clicked Sign-In CTA',
    });

    store.setState({
      submitting: true,
    });

    return session.actions
      .createSession({
        email,
        password,
        redirectPath: deeplinkPath,
      })
      .then(async (authenticated) => {
        if (authenticated) {
          log('upd.sign_in.event.succeeded');
          track({
            verb: TRACK,
            object: 'signin',
            segmentEventName: 'Signed In',
          });
          session.actions.setResetPassword(false);
          session.actions.setForgotPassword(false);
          const redirected = await redirectIntoQuickSetup();
          const redirectedIntoConsumerApp = await redirectIntoConsumerApp();
          if (redirectedIntoConsumerApp) {
            console.log('REDIRECTED INTO CONSUMER APP');
            window.location.assign(ENVIRONMENT.endpoints.consumerAppUrl);
            return authenticated;
          }
          if (redirected) {
            return authenticated;
          }
          if (deeplinkPath === '/') {
            const daisyChainRoute = daisyChain.selectors.nextStepRoute();
            const route = daisyChainRoute
              ? `${daisyChainRoute}?showIntroBanner=true`
              : '/';
            router.go(route);
          } else {
            router.go(deeplinkPath);
          }
        } else {
          log('upd.sign_in.event.failed');
          const unauthorizedMessage = `Oops! 
            The email or password entered doesn't match our records.
            Please check them and try again.`;
          const lockedOutMessage = `Uh oh! Looks like you forgot your password. 
            Your account has been locked due to 5 bad login attempts. 
            To reset your password please click "I forgot my password" below.`;
          const isLockedOut = session.selectors.isLockedOut(session.getState());
          const errorMessage = isLockedOut
            ? lockedOutMessage
            : unauthorizedMessage;
          store.setState({
            errorMessage,
            submitting: false,
          });
        }

        return authenticated;
      });
  }

  return {
    subscribe: store.subscribe,
    connect: (fn) => {
      fn(store.getState());
      return store.subscribe((state) => fn(state));
    },
    getState: store.getState,
    actions: {
      handleForgotPassword,
      handleEmailChange,
      handlePasswordChange,
      handleSubmit,
    },
    destroy: () => {},
  };
}
